'use strict';

// ===========================================================================
// General utilities
// ===========================================================================

/**
 * Debounce a function call.
 */
export function debounce(func, wait, immediate) {
    let timeout;
    return function (...args) {
        const callNow = immediate && !timeout;
        clearTimeout(timeout);
        timeout = setTimeout(() => {
            timeout = null;
            if (!immediate) func.apply(this, args);
        }, wait);
        if (callNow) func.apply(this, args);
    };
}

/**
 * Delegate an event: listens on `document` and fires `handler` only when
 * the event target (or an ancestor) matches `selector`.
 */
export function delegate(eventType, selector, handler, options) {
    const listener = (event) => {
        const target = event.target?.closest?.(selector);
        if (target) handler.call(target, event);
    };
    document.addEventListener(eventType, listener, options);
    return () => document.removeEventListener(eventType, listener, options);
}

/**
 * POST helper — sends form-encoded data via fetch, returns parsed JSON.
 */
export function postJSON(url, data, extraHeaders = {}) {
    return fetch(url, {
        method: 'POST',
        headers: {
            'X-Requested-With': 'XMLHttpRequest',
            ...extraHeaders,
        },
        body: new URLSearchParams(data),
    }).then((res) => {
        if (!res.ok) throw new Error(res.status + ' ' + res.statusText);
        return res.json();
    });
}

/**
 * Make a container clickable — clicking anywhere on it navigates to the
 * first <a> found inside.
 */
export function clickableContainer(selector) {
    delegate('click', selector, function (event) {
        if (event.target.closest('a')) return;
        event.preventDefault();
        const link = this.querySelector('a[href]');
        if (link) window.location = link.href;
    });
}

// ===========================================================================
// Animation helpers (replace jQuery slideUp / slideDown / fadeOut)
// ===========================================================================

export function slideDown(el, duration = 300) {
    el.style.display = '';
    el.style.overflow = 'hidden';
    const height = el.scrollHeight;
    el.style.height = '0';
    el.style.transition = `height ${duration}ms ease`;

    el.offsetHeight; // force reflow

    el.style.height = height + 'px';

    setTimeout(() => {
        el.style.height = '';
        el.style.overflow = '';
        el.style.transition = '';
    }, duration);
}

export function slideUp(el, duration = 300, callback) {
    el.style.overflow = 'hidden';
    el.style.height = el.offsetHeight + 'px';
    el.style.transition = `height ${duration}ms ease`;

    el.offsetHeight; // force reflow

    el.style.height = '0';

    setTimeout(() => {
        el.style.display = 'none';
        el.style.height = '';
        el.style.overflow = '';
        el.style.transition = '';
        if (typeof callback === 'function') callback();
    }, duration);
}

export function fadeOutSlideUpRemove(el, fadeDuration = 300, slideDuration = 300) {
    el.style.transition = `opacity ${fadeDuration}ms ease`;
    el.style.opacity = '0';

    setTimeout(() => {
        el.style.overflow = 'hidden';
        el.style.transition = `height ${slideDuration}ms ease, `
            + `margin ${slideDuration}ms ease, `
            + `padding ${slideDuration}ms ease`;
        el.style.height = el.offsetHeight + 'px';

        el.offsetHeight; // force reflow

        el.style.height = '0';
        el.style.margin = '0';
        el.style.padding = '0';

        setTimeout(() => el.remove(), slideDuration);
    }, fadeDuration);
}

// ===========================================================================
// Form helpers
// ===========================================================================

/**
 * Email validation.
 */
export function isValidEmail(email) {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(email);
}

/**
 * Auto-expand a textarea to fit its content.
 */
export function autoExpand(field) {
    field.style.height = 'inherit';

    const computed = window.getComputedStyle(field);

    const height = parseInt(computed.getPropertyValue('border-top-width'), 10)
        + parseInt(computed.getPropertyValue('padding-top'), 10)
        + field.scrollHeight
        + parseInt(computed.getPropertyValue('padding-bottom'), 10)
        + parseInt(computed.getPropertyValue('border-bottom-width'), 10);

    field.style.height = height + 'px';
}

/**
 * Attach auto-expand to all textareas via a single document-level listener.
 * Safe to call multiple times — only binds once.
 */
let autoExpandBound = false;
export function bindAutoExpand() {
    if (autoExpandBound) return;
    autoExpandBound = true;
    document.addEventListener('input', (event) => {
        if (event.target.tagName.toLowerCase() === 'textarea') {
            autoExpand(event.target);
        }
    }, false);
}


// ===========================================================================
// reCAPTCHA Enterprise (score-based, replaces v3 classic)
// ===========================================================================

/* global config */

const RECAPTCHA_SITE_KEY = config?.recaptchaSiteKey || '';

let recaptchaLoadPromise = null;

/**
 * Load the reCAPTCHA Enterprise script once. Returns a Promise.
 */
export function loadRecaptchaScript(lang) {
    // Already available
    if (typeof window.grecaptcha !== 'undefined'
        && window.grecaptcha.enterprise?.execute) {
        return Promise.resolve();
    }

    // Already loading — reuse the same promise
    if (recaptchaLoadPromise) return recaptchaLoadPromise;

    recaptchaLoadPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        let src = 'https://www.google.com/recaptcha/enterprise.js?render='
            + encodeURIComponent(RECAPTCHA_SITE_KEY);

        if (lang) {
            src += '&hl=' + encodeURIComponent(lang);
        }

        script.src = src;
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load reCAPTCHA script'));

        document.head.appendChild(script);
    });

    return recaptchaLoadPromise;
}

/**
 * Request a reCAPTCHA Enterprise token for a given action.
 * Returns a Promise<string>.
 */
export function getRecaptchaToken(action, lang) {
    return loadRecaptchaScript(lang).then(() => {
        return new Promise((resolve, reject) => {
            window.grecaptcha.enterprise.ready(() => {
                window.grecaptcha.enterprise.execute(RECAPTCHA_SITE_KEY, { action })
                    .then((token) => {
                        if (!token) {
                            reject(new Error('Empty reCAPTCHA token'));
                            return;
                        }
                        resolve(token);
                    })
                    .catch(reject);
            });
        });
    });
}


// ===========================================================================
// reCAPTCHA v3 (classic way)
// ===========================================================================
//
// /* global config */
//
// const RECAPTCHA_SITE_KEY = config?.recaptchaSiteKey
//     || config?.globals?.recaptchaSiteKey
//     || '';
//
// let recaptchaLoadPromise = null;
//
// /**
//  * Load the reCAPTCHA v3 script once. Returns a Promise.
//  */
// export function loadRecaptchaScript(lang) {
//     // Already available
//     if (typeof window.grecaptcha !== 'undefined' && window.grecaptcha.execute) {
//         return Promise.resolve();
//     }
//
//     // Already loading — reuse the same promise
//     if (recaptchaLoadPromise) return recaptchaLoadPromise;
//
//     recaptchaLoadPromise = new Promise((resolve, reject) => {
//         const script = document.createElement('script');
//         let src = 'https://www.google.com/recaptcha/api.js?render='
//             + encodeURIComponent(RECAPTCHA_SITE_KEY);
//
//         if (lang) {
//             src += '&hl=' + encodeURIComponent(lang);
//         }
//
//         script.src = src;
//         script.async = true;
//         script.defer = true;
//         script.onload = () => resolve();
//         script.onerror = () => reject(new Error('Failed to load reCAPTCHA script'));
//
//         document.head.appendChild(script);
//     });
//
//     return recaptchaLoadPromise;
// }
//
// /**
//  * Request a reCAPTCHA v3 token for a given action. Returns a Promise<string>.
//  */
// export function getRecaptchaToken(action, lang) {
//     return loadRecaptchaScript(lang).then(() => {
//         return new Promise((resolve, reject) => {
//             window.grecaptcha.ready(() => {
//                 window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action })
//                     .then((token) => {
//                         if (!token) {
//                             reject(new Error('Empty reCAPTCHA token'));
//                             return;
//                         }
//                         resolve(token);
//                     })
//                     .catch(reject);
//             });
//         });
//     });
// }
